import React, { useState, useEffect, useContext } from 'react';
import { getTwoLetterLanguageCode } from 'helpers';
import { useAppContext } from 'context/AppContext';
import { isLandrAppHostname } from 'helpers/isLandrAppHostname';
import { EventId } from 'enum/eventId';
import { log } from '../../log';

const logger = log.getInstance('AuthenticationProvider');

const makeCollaborationReturnPath = (id: string, type: string, code: string): string => {
    if (type === 'Audio') {
        return `/library/tracks/${id}?collaborationCode=${code}&referrer=authsite`;
    } else if (type === 'Project') {
        return `/library/projects/${id}?collaborationCode=${code}&referrer=authsite`;
    } else {
        return '';
    }
};

const fetchCollaborationReturnPath = (code: string): Promise<string> => {
    return fetch(`${process.env.GATSBY_API_ENDPOINT}/library/api/v1/collaborators/link/${code}`)
        .then((response) => response.json())
        .then((item) => (item ? makeCollaborationReturnPath(item.itemId, item.itemType, code) : ''))
        .catch(() => {
            return '';
        });
};

/**
 *
 * @param redirectUrl
 * @param currentPageParams
 * @param pageLang - Current guest site language
 * @param homePage - The homePage asignation on signup, set for each prismic page
 */
const augmentQueryParams = (
    redirectUrl: URL,
    currentPageParams: URLSearchParams,
    pageLang: string,
    homePage: string | null,
) => {
    const redirectUrlParams = new URLSearchParams(redirectUrl.search);

    if (pageLang) {
        redirectUrlParams.set('locale', getTwoLetterLanguageCode(pageLang));
    }

    if (homePage) {
        redirectUrlParams.set('homePage', homePage);
    }

    // Merge currentPageParams and redirectUrlParams, only if redirectUrlParams doesn't already contain the param
    for (const [key, value] of currentPageParams.entries()) {
        if (!redirectUrlParams.has(key) && key !== 'returnUrl') {
            redirectUrlParams.append(key, value);
        }
    }
    return redirectUrlParams;
};

type MakeRedirectUrlResult = { redirectUrl: URL; redirectUrlParams: URLSearchParams };

const makeRedirectUrl = async (
    currentPageParams: URLSearchParams,
    pageLang: string,
    homePage: string | null,
): Promise<MakeRedirectUrlResult> => {
    const appEndpoint = process.env.GATSBY_APP_ENDPOINT as string;
    const plan = currentPageParams.get('plan');
    const planFrequency = currentPageParams.get('planFrequency');
    const collaborationCode = currentPageParams.get('collaborationCode');
    const returnUrl = currentPageParams.get('returnUrl');
    let redirectUrl: URL | null = null;

    // Determine where to redirect the user after registering & logging in
    if (plan) {
        // User has chosen a billing plan
        redirectUrl = new URL(`${appEndpoint}/payment/subscription/checkout/${plan}/${planFrequency || 'yearly'}`);
    } else if (collaborationCode) {
        // Invitation to collaborate on an Asset/Project
        redirectUrl = new URL(appEndpoint + (await fetchCollaborationReturnPath(collaborationCode)));
    } else if (returnUrl) {
        // Use a try/catch in case we are unable to parse the URLs
        try {
            // User is supposed to be redirected to a URL specified in the returnUrl query param
            const returnUrlObject = new URL(returnUrl);
            // Only allow redirections to LANDR apps to prevent malicious redirections
            if (isLandrAppHostname(returnUrlObject.hostname)) {
                redirectUrl = returnUrlObject;
            } else {
                // Log a warning
                logger.warn(
                    'An unsupported returnUrl was used as a returnUrl query param',
                    EventId.UnsupportedReturnUrlWarning,
                );
            }
        } catch (e) {
            // This error will be caught if we are unable to parse the URL using new URL(...)
            logger.warn(e.message, EventId.CatchedReturnUrlWarning);
        }
    }

    if (!redirectUrl) {
        // Send the user to the web app home page
        redirectUrl = new URL(appEndpoint);
    }

    return {
        redirectUrl,
        redirectUrlParams: augmentQueryParams(redirectUrl, currentPageParams, pageLang, homePage),
    };
};

export type AuthenticationContextType = {
    loginUrl: string;
    signupUrl: string;
};

const AuthenticationContext = React.createContext({
    loginUrl: '',
    signupUrl: '',
} as AuthenticationContextType);

const useAuthentication = (): AuthenticationContextType => useContext(AuthenticationContext);

interface AuthenticationProviderProps {
    children: React.ReactNode;
}

const AuthenticationProvider: React.FC = ({ children }: AuthenticationProviderProps) => {
    const href = typeof window !== 'undefined' && window.location.href;
    const searchParams = typeof window !== 'undefined' && window.location.search;
    const [loginUrl, setLoginUrl] = useState('');
    const [signupUrl, setSignupUrl] = useState('');
    const appContext = useAppContext();

    useEffect(() => {
        const makeUrl = async () => {
            if (typeof window !== 'undefined') {
                const currentPageParams = new URLSearchParams(window.location.search);

                const { redirectUrl, redirectUrlParams } = await makeRedirectUrl(
                    currentPageParams,
                    appContext.pageContext.lang,
                    appContext.homePage,
                );

                let url = `${redirectUrl.origin}${redirectUrl.pathname}`;

                if (redirectUrlParams && !!redirectUrlParams.toString()) {
                    url += `?${redirectUrlParams.toString()}`;
                }

                setLoginUrl(url);
                setSignupUrl(`${url}${url.includes('?') ? '&' : '?'}isSignup=true`);
            }
        };

        makeUrl();
    }, [appContext.pageContext.lang, appContext.homePage, searchParams, href]);

    return (
        <AuthenticationContext.Provider
            value={{
                loginUrl,
                signupUrl,
            }}
        >
            <>{children}</>
        </AuthenticationContext.Provider>
    );
};

export { AuthenticationProvider, useAuthentication };
